import {
    SRGBColorSpace,
    LoadingManager,
    FileLoader,
    TextureLoader,
    Scene,
    WebGLRenderer,
    PerspectiveCamera,
    MathUtils
} from 'three';
import { debounce } from "../../../utils";
import BenMarcHeroWorld from "./BenMarcHeroWorld";

export default class BenMarcHero {
    static create() {
        const el = document.getElementById('ben-marc--hero-animation')
        if(el) {
            new BenMarcHero(el);
        }
    }

    constructor(el) {
        this.el = el;
        this.setSizes();
        this.binds();
        this.initAssets();
        this.initLoader();
        this.initDOM();
    }

    binds() {
        this.render = this.render.bind(this);
        this.update = this.update.bind(this);
        this.onResize = this.onResize.bind(this);
    }

    initAssets() {
        this.textures = {
            drawing: {
                filename: 'drawing-texture.png',
            },
            logo: {
                filename: 'hero-logo.png',
            },
            digit0: {
                filename: 'digit-0.png',
            },
            digit1: {
                filename: 'digit-1.png',
            }
        };
    }

    initDOM() {
        const parent = document.getElementById('ben-marc--hero');
        const container = parent.querySelector('.container')
        this.DOM = {
            parent: {
                el: parent
            },
            container: {
                el: container,
                width: container.offsetWidth,
                height: container.offsetHeight
            }
        }
    }

    initLoader() {
        this.manager = new LoadingManager();
        this.loader = new FileLoader(this.manager);
        this.textureLoader = new TextureLoader(this.manager);
        this.assetsUrl = window.assets_url;

        Object.keys(this.textures).forEach((key) => {
            const textureInfo = this.textures[key];
            const filePath = `${this.assetsUrl}/images/ben-marc/${textureInfo.filename}`;

            this.textureLoader.load(filePath, (texture) => {
                texture.colorSpace = SRGBColorSpace;

                this.textures[key] = {
                    map: texture,
                    width: texture.image.width,
                    height: texture.image.height
                };
            });
        });

        this.manager.onLoad = () => {
            this.init();
        }
    }

    setSizes() {
        this.sizes = {
            width: this.el.offsetWidth,
            height: this.el.offsetHeight,
            dpr: Math.min(window.devicePixelRatio, 2),
            aspectRatio: this.el.clientWidth / this.el.clientHeight
        }
    }

    isMobile() {
        return this.sizes.width < 768;
    }

    update() {

    }

    createWorld() {
        this.world = new BenMarcHeroWorld(this);
        this.scene.add(this.world.world);
    }

    createScene() {
        this.scene = new Scene();
    }

    createCamera() {
        this.camera = new PerspectiveCamera(75, this.sizes.aspectRatio, 0.1, 100);
        this.camera.position.z = 20;
    }

    createRenderer() {
        this.renderer = new WebGLRenderer({
            antialias: true,
        });
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.renderer.setPixelRatio(this.sizes.dpr);
        this.el.appendChild(this.renderer.domElement);
    }

    addListeners() {
        this.initResizeObserver();
    }

    initResizeObserver() {
        this.resizeObserver = new ResizeObserver(debounce(this.onResize, 100));
        this.resizeObserver.observe(this.el);
    }

    onResize() {
        if(this.sizes.width === this.el.offsetWidth && this.sizes.height === this.el.offsetHeight) return;

        this.sizes.widthPrev = this.sizes.width;
        this.sizes.heightPrev = this.sizes.height;
        this.sizes.width = this.el.offsetWidth;
        this.sizes.height = this.el.offsetHeight;
        this.sizes.aspectRatio = this.sizes.width / this.sizes.height;

        this.DOM.container.width = this.DOM.container.el.offsetWidth;
        this.DOM.container.height = this.DOM.container.el.offsetHeight;

        this.camera.aspect = this.sizes.aspectRatio;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(this.sizes.width, this.sizes.height);
        this.updateCameraSizes();

        this.world.resize();
    }

    updateCameraSizes() {
        this.sizes.vFOV = MathUtils.degToRad(this.camera.fov);
        this.sizes.emHeight = 2 * Math.tan(this.sizes.vFOV / 2) * this.camera.position.z;
        this.sizes.emWidth = this.sizes.emHeight * this.sizes.aspectRatio;
        this.sizes.pixelSize = this.sizes.emWidth / this.sizes.width;
    }

    init() {
        this.createScene();
        this.createCamera();
        this.createRenderer();
        this.updateCameraSizes();
        this.createWorld();
        this.addListeners();
        this.render();
    }

    render() {
        this.update();
        this.renderer.render(this.scene, this.camera);
        requestAnimationFrame(this.render);
    }
}
